"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Eye, MailCheck, ShieldCheck, TrendingUp, ArrowRight, CircleCheck as CheckCircle2, UserCheck, ChevronRight } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Link } from "@/components/common/Link";
const stepsData = [{
  id: 1,
  number: "01",
  title: "Organic Visibility",
  subtitle: "High-Intent Commercial Reach",
  whatItMeans: "Visibility on relevant commercial terms rather than superficial traffic volume. The focus is making sure your high-value service and solution pages appear when buyers are actively searching with genuine commercial intent.",
  deliverable1: "Non-branded commercial keyword visibility mapped to core offerings",
  deliverable2: "Relevant organic traffic focused on commercial and solution landing pages",
  deliverable3: "Technical health and crawlability audits to ensure search engines discover key pages",
  whatILookAt: "Rankings on non-branded commercial terms, landing page impressions, and crawl hygiene."
}, {
  id: 2,
  number: "02",
  title: "Enquiries",
  subtitle: "Commercial Landing Pages & CTAs",
  whatItMeans: "Turning search visits into direct touchpoints: consultation requests, demo bookings, and quotation forms. I evaluate whether visitors find the answers they need to take the next step.",
  deliverable1: "Commercial landing pages tailored with clear, procurement-stage calls to action",
  deliverable2: "Frictionless enquiry forms and direct calendar booking links",
  deliverable3: "Intent alignment so each page directly answers what the searcher anticipated",
  whatILookAt: "Conversion rates on priority goals, enquiry form submissions, and meeting requests."
}, {
  id: 3,
  number: "03",
  title: "Sales-Qualified Leads",
  subtitle: "Lead Criteria & Feedback Loops",
  whatItMeans: "Differentiating raw form fills from leads that fit your ideal customer profile in company size, industry, and stated need. Sales feedback directly informs search strategy adjustments.",
  deliverable1: "Distinction between basic form fills, marketing-qualified leads, and sales-ready prospects",
  deliverable2: "Tagging and tracking organic-source leads inside the CRM where supported",
  deliverable3: "Regular reviews with sales feedback to refine keyword targets and content priorities",
  whatILookAt: "Lead qualification criteria, target sector alignment, and sales team feedback."
}, {
  id: 4,
  number: "04",
  title: "Pipeline Contribution",
  subtitle: "Commercial Value & Leadership Reporting",
  whatItMeans: "Understanding how organic search contributes to active sales opportunities and pipeline value. While B2B attribution is rarely perfectly clean, tracking progress against commercial objectives keeps efforts grounded.",
  deliverable1: "Tracking number and value of active deals connected to organic search touchpoints",
  deliverable2: "Goal configuration and journey tracking in analytics platforms",
  deliverable3: "Concise reporting for leadership expressed in straightforward commercial language",
  whatILookAt: "Trackable deal values, pipeline stage progression, and commercial reporting metrics."
}];
export default function SearchProcess() {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const prefersReducedMotion = useReducedMotion();
  const activeStep = stepsData[activeStepIndex];
  const progressPercent = (activeStepIndex + 1) / stepsData.length * 100;
  const getStepIcon = (id: number) => {
    switch (id) {
      case 1:
        return <Eye className="size-5 text-primary shrink-0" />;
      case 2:
        return <MailCheck className="size-5 text-primary shrink-0" />;
      case 3:
        return <ShieldCheck className="size-5 text-primary shrink-0" />;
      case 4:
        return <TrendingUp className="size-5 text-primary shrink-0" />;
      default:
        return <Eye className="size-5 text-primary shrink-0" />;
    }
  };
  return <section id="b2bsearchprocess" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 transition-colors duration-200">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">
            Lead quality & measurement
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default mb-6">
            B2B lead generation through organic search
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed font-default">
            Rankings alone are not success. The aim is qualified, trackable enquiries your sales team can work. Attribution in B2B is never perfectly clean, which is why I focus on lead quality and commercial relevance at every stage of the journey.
          </p>
        </div>

        {/* Progress tracker for desktop */}
        <div className="max-w-4xl mx-auto mb-10 hidden md:block">
          <div className="flex justify-between text-xs font-semibold text-muted-foreground mb-2">
            <span>Stage {activeStepIndex + 1} of {stepsData.length}</span>
            <span>{Math.round(progressPercent)}% Ladder Progression</span>
          </div>
          <Progress value={progressPercent} className="h-2 rounded-full" />
        </div>

        {/* Interactive Step Explorer */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-start">
          {/* Step navigation buttons */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            {stepsData.map((step, i) => {
            const isActive = i === activeStepIndex;
            return <div data-index={i} key={step.id}>
                  <Button variant={isActive ? "default" : "outline"} onClick={() => setActiveStepIndex(i)} className={`w-full justify-start h-auto p-5 text-left rounded-xl transition-all duration-200 shadow-sm border ${isActive ? "bg-primary text-primary-foreground border-primary" : "bg-card text-card-foreground border-border hover:bg-muted"}`}>
                    <div className="flex items-center gap-4 w-full">
                      <div className={`size-10 rounded-lg flex items-center justify-center font-bold text-sm shrink-0 ${isActive ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-foreground"}`}>
                        {step.number}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-base truncate font-default">
                          {step.title}
                        </div>
                      </div>
                      <ChevronRight className={`size-5 shrink-0 transition-transform ${isActive ? "translate-x-1 text-primary-foreground" : "text-muted-foreground"}`} />
                    </div>
                  </Button>
                </div>;
          })}

            {/* Realistic expectations note */}
            <Card className="bg-muted/50 border-border border rounded-xl mt-2">
              <CardContent className="p-4 flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg shrink-0 mt-0.5">
                  <UserCheck className="size-5 text-primary" />
                </div>
                <div className="text-xs text-muted-foreground leading-relaxed">
                  I make no promises on lead volumes, rankings or sales. The aim is better lead quality and a clearer link between organic search and your pipeline.
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed step panel */}
          <div className="lg:col-span-7">
            <motion.div key={activeStep.id} initial={prefersReducedMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.35,
            ease: "easeOut"
          }}>
              <Card className="bg-card text-card-foreground border-border border shadow-md rounded-2xl overflow-hidden">
                <CardContent className="p-6 sm:p-8 flex flex-col gap-6">
                  {/* Step header */}
                  <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-6">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-primary/10 rounded-xl">
                        {getStepIcon(activeStep.id)}
                      </div>
                      <div>
                        <span className="text-xs font-bold text-primary uppercase tracking-widest block">
                          Stage {activeStep.number}
                        </span>
                        <h3 className="text-2xl font-bold text-foreground font-default">
                          {activeStep.title}
                        </h3>
                      </div>
                    </div>
                    <Badge variant="outline" className="px-3 py-1 text-xs font-medium border-border">
                      {activeStep.subtitle}
                    </Badge>
                  </div>

                  {/* Description */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-foreground uppercase tracking-wider">
                      What this stage means
                    </h4>
                    <p className="text-muted-foreground leading-relaxed text-base font-default">
                      {activeStep.whatItMeans}
                    </p>
                  </div>

                  {/* Deliverables / Actions */}
                  <div className="space-y-3 bg-muted/30 p-5 rounded-xl border border-border">
                    <h4 className="text-sm font-semibold text-foreground font-default flex items-center gap-2">
                      <CheckCircle2 className="size-4 text-primary" /> Key areas of focus:
                    </h4>
                    <ul className="grid grid-cols-1 gap-2.5 text-sm text-foreground">
                      <li className="flex items-start gap-2">
                        <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                        <span>{activeStep.deliverable1}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                        <span>{activeStep.deliverable2}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                        <span>{activeStep.deliverable3}</span>
                      </li>
                    </ul>
                  </div>

                  {/* What I look at & next step */}
                  <div className="p-4 bg-primary/5 rounded-xl border border-primary/20 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <span className="text-xs text-muted-foreground uppercase font-semibold block">
                        What I look at
                      </span>
                      <span className="text-sm font-semibold text-foreground font-default">
                        {activeStep.whatILookAt}
                      </span>
                    </div>
                    <Button size="sm" onClick={() => {
                    if (activeStepIndex < stepsData.length - 1) {
                      setActiveStepIndex(prev => prev + 1);
                    } else {
                      setActiveStepIndex(0);
                    }
                  }} className="shrink-0 w-full sm:w-auto">
                      {activeStepIndex === stepsData.length - 1 ? "Return to Start" : "Next Stage"}
                      <ArrowRight className="size-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>

        {/* 4-Step Summary Grid */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h3 className="text-2xl sm:text-3xl font-semibold text-foreground font-default mb-3">
              The four stages at a glance
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base max-w-2xl mx-auto font-default">
              Moving from raw organic visibility through to qualified enquiries, sales qualification, and pipeline tracking.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stepsData.map((step, i) => <div data-index={i} key={step.id}>
                <Card className="bg-card text-card-foreground border-border border shadow-sm rounded-xl h-full flex flex-col hover:shadow-md transition-shadow">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl font-bold text-primary font-mono">
                        {step.number}
                      </span>
                      <div className="p-2 bg-muted rounded-lg">
                        {getStepIcon(step.id)}
                      </div>
                    </div>
                    <h4 className="text-lg font-semibold text-foreground font-default mb-2">
                      {step.title}
                    </h4>
                    <p className="text-xs text-muted-foreground leading-relaxed font-default mb-6 flex-1">
                      {step.whatItMeans}
                    </p>
                    <Button variant="ghost" size="sm" onClick={() => {
                  setActiveStepIndex(i);
                  window.scrollTo({
                    top: document.getElementById("b2bsearchprocess")?.offsetTop || 0,
                    behavior: "smooth"
                  });
                }} className="w-full justify-between text-xs text-primary hover:text-primary hover:bg-primary/10 mt-auto">
                      <span>Inspect Stage</span>
                      <ChevronRight className="size-3.5" />
                    </Button>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>

        {/* Consultation Callout */}
        <div className="mt-16 bg-card border-border border rounded-2xl p-8 sm:p-10 shadow-md text-center max-w-4xl mx-auto flex flex-col items-center gap-6">
          <Badge variant="secondary" className="px-3 py-1 text-xs">
            Direct SEO Advice
          </Badge>
          <h3 className="text-2xl sm:text-3xl font-bold text-foreground font-default">
            Want to see how organic search could feed your pipeline?
          </h3>
          <p className="text-muted-foreground text-sm sm:text-base max-w-xl font-default leading-relaxed">
            Discuss your sales cycle, target accounts, and current search visibility directly with me. I will review where your organic enquiries are coming from and what needs refining.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 w-full sm:w-auto">
            <Button asChild size="lg" className="rounded-md font-medium w-full sm:w-auto">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild className="rounded-md font-medium w-full sm:w-auto">
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}